import { useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import {
  bulkOperationsAPI,
  bulkOperationUtils,
  type BulkOperationPayload,
  type BulkAssignPayload,
  type BulkTagPayload,
  type BulkFlagPayload,
  type TeamMember
} from '../services/bulkOperationsAPI'
import {
  clearConversationSelection,
  GetAllData
} from '../pages/UserPages/inbox-v2/InboxSlice/InboxSlice'

interface BulkOperationState {
  loading: boolean
  error: string | null
  success: boolean
}

interface UseBulkOperationsReturn {
  // State
  state: BulkOperationState
  
  // Operations
  assignConversations: (conversationIds: string[], assigneeId: string) => Promise<void>
  archiveConversations: (conversationIds: string[]) => Promise<void>
  unsubscribeConversations: (conversationIds: string[]) => Promise<void>
  addTagsToConversations: (conversationIds: string[], tagIds: string[]) => Promise<void>
  removeTagsFromConversations: (conversationIds: string[], tagIds: string[]) => Promise<void>
  markConversationsAsRead: (conversationIds: string[]) => Promise<void>
  markConversationsAsUnread: (conversationIds: string[]) => Promise<void>
  closeConversationsAsRead: (conversationIds: string[]) => Promise<void>
  flagConversations: (conversationIds: string[], flagStatus?: boolean) => Promise<void>
  deleteConversations: (conversationIds: string[]) => Promise<void>
  restoreConversations: (conversationIds: string[]) => Promise<void>
  moveConversationsToSpam: (conversationIds: string[]) => Promise<void>
  exportConversations: (conversationIds: string[], format?: 'csv' | 'xlsx' | 'json') => Promise<void>
  setPriorityForConversations: (conversationIds: string[], priority: 'low' | 'normal' | 'high' | 'urgent') => Promise<void>
  addNotesToConversations: (conversationIds: string[], note: string, isPrivate?: boolean) => Promise<void>
  changeConversationStatus: (conversationIds: string[], status: 'open' | 'pending' | 'resolved' | 'closed') => Promise<void>
  
  // Utility functions
  resetState: () => void
  validateAndExecute: (conversationIds: string[], operation: () => Promise<void>) => Promise<void>
}

export const useBulkOperations = (options?: {
  refreshOnSuccess?: boolean
  showSuccessToast?: boolean
  showErrorToast?: boolean
  clearSelectionOnSuccess?: boolean
}): UseBulkOperationsReturn => {
  const dispatch = useDispatch()
  
  const {
    refreshOnSuccess = true,
    showSuccessToast = true,
    showErrorToast = true,
    clearSelectionOnSuccess = true
  } = options || {}

  const [state, setState] = useState<BulkOperationState>({
    loading: false,
    error: null,
    success: false
  })

  const resetState = useCallback(() => {
    setState({
      loading: false,
      error: null,
      success: false
    })
  }, [])

  const validateAndExecute = useCallback(async (
    conversationIds: string[],
    operation: () => Promise<void>
  ) => {
    if (!bulkOperationUtils.validateConversationIds(conversationIds)) {
      const error = 'Invalid conversation IDs provided'
      setState(prev => ({ ...prev, error }))
      if (showErrorToast) {
        toast.error(error)
      }
      return
    }

    await operation()
  }, [showErrorToast])

  const executeOperation = useCallback(async (
    operationName: string,
    operationFn: () => Promise<any>,
    conversationCount: number
  ) => {
    setState(prev => ({ ...prev, loading: true, error: null, success: false }))

    try {
      const result = await operationFn()
      
      setState(prev => ({ ...prev, loading: false, success: true }))
      
      if (showSuccessToast) {
        const message = bulkOperationUtils.formatSuccessMessage(operationName, conversationCount)
        toast.success(message)
      }

      if (clearSelectionOnSuccess) {
        dispatch(clearConversationSelection())
      }

      if (refreshOnSuccess) {
        // Refresh inbox data
        dispatch(GetAllData({ sortField: 'createdAt', sortOrder: 'descend', size: 50 }))
      }

      return result
    } catch (error: any) {
      const errorMessage = error.message || `Failed to ${operationName} conversations`
      setState(prev => ({ ...prev, loading: false, error: errorMessage }))
      
      if (showErrorToast) {
        toast.error(errorMessage)
      }
      
      throw error
    }
  }, [dispatch, showSuccessToast, showErrorToast, clearSelectionOnSuccess, refreshOnSuccess])

  // Individual operation methods
  const assignConversations = useCallback(async (conversationIds: string[], assigneeId: string) => {
    await validateAndExecute(conversationIds, async () => {
      await executeOperation(
        'assign',
        () => bulkOperationsAPI.bulkAssign({ conversationIds, assigneeId }),
        conversationIds.length
      )
    })
  }, [validateAndExecute, executeOperation])

  const archiveConversations = useCallback(async (conversationIds: string[]) => {
    await validateAndExecute(conversationIds, async () => {
      await executeOperation(
        'archive',
        () => bulkOperationsAPI.bulkArchive({ conversationIds }),
        conversationIds.length
      )
    })
  }, [validateAndExecute, executeOperation])

  const unsubscribeConversations = useCallback(async (conversationIds: string[]) => {
    await validateAndExecute(conversationIds, async () => {
      await executeOperation(
        'unsubscribe',
        () => bulkOperationsAPI.bulkUnsubscribe({ conversationIds }),
        conversationIds.length
      )
    })
  }, [validateAndExecute, executeOperation])

  const addTagsToConversations = useCallback(async (conversationIds: string[], tagIds: string[]) => {
    await validateAndExecute(conversationIds, async () => {
      await executeOperation(
        'addTags',
        () => bulkOperationsAPI.bulkAddTags({ conversationIds, tagIds }),
        conversationIds.length
      )
    })
  }, [validateAndExecute, executeOperation])

  const removeTagsFromConversations = useCallback(async (conversationIds: string[], tagIds: string[]) => {
    await validateAndExecute(conversationIds, async () => {
      await executeOperation(
        'removeTags',
        () => bulkOperationsAPI.bulkRemoveTags({ conversationIds, tagIds }),
        conversationIds.length
      )
    })
  }, [validateAndExecute, executeOperation])

  const markConversationsAsRead = useCallback(async (conversationIds: string[]) => {
    await validateAndExecute(conversationIds, async () => {
      await executeOperation(
        'markAsRead',
        () => bulkOperationsAPI.bulkMarkAsRead({ conversationIds }),
        conversationIds.length
      )
    })
  }, [validateAndExecute, executeOperation])

  const markConversationsAsUnread = useCallback(async (conversationIds: string[]) => {
    await validateAndExecute(conversationIds, async () => {
      await executeOperation(
        'markAsUnread',
        () => bulkOperationsAPI.bulkMarkAsUnread({ conversationIds }),
        conversationIds.length
      )
    })
  }, [validateAndExecute, executeOperation])

  const closeConversationsAsRead = useCallback(async (conversationIds: string[]) => {
    await validateAndExecute(conversationIds, async () => {
      await executeOperation(
        'closeAsRead',
        () => bulkOperationsAPI.bulkCloseAsRead({ conversationIds }),
        conversationIds.length
      )
    })
  }, [validateAndExecute, executeOperation])

  const flagConversations = useCallback(async (conversationIds: string[], flagStatus: boolean = true) => {
    await validateAndExecute(conversationIds, async () => {
      await executeOperation(
        flagStatus ? 'flag' : 'unflag',
        () => bulkOperationsAPI.bulkFlag({ conversationIds, isFlagged: flagStatus }),
        conversationIds.length
      )
    })
  }, [validateAndExecute, executeOperation])

  const deleteConversations = useCallback(async (conversationIds: string[]) => {
    await validateAndExecute(conversationIds, async () => {
      await executeOperation(
        'delete',
        () => bulkOperationsAPI.bulkDelete({ conversationIds }),
        conversationIds.length
      )
    })
  }, [validateAndExecute, executeOperation])

  const restoreConversations = useCallback(async (conversationIds: string[]) => {
    await validateAndExecute(conversationIds, async () => {
      await executeOperation(
        'restore',
        () => bulkOperationsAPI.bulkRestore({ conversationIds }),
        conversationIds.length
      )
    })
  }, [validateAndExecute, executeOperation])

  const moveConversationsToSpam = useCallback(async (conversationIds: string[]) => {
    await validateAndExecute(conversationIds, async () => {
      await executeOperation(
        'moveToSpam',
        () => bulkOperationsAPI.bulkMoveToSpam({ conversationIds }),
        conversationIds.length
      )
    })
  }, [validateAndExecute, executeOperation])

  const exportConversations = useCallback(async (
    conversationIds: string[], 
    format: 'csv' | 'xlsx' | 'json' = 'csv'
  ) => {
    await validateAndExecute(conversationIds, async () => {
      await executeOperation(
        'export',
        () => bulkOperationsAPI.bulkExport({ conversationIds, format }),
        conversationIds.length
      )
    })
  }, [validateAndExecute, executeOperation])

  const setPriorityForConversations = useCallback(async (
    conversationIds: string[], 
    priority: 'low' | 'normal' | 'high' | 'urgent'
  ) => {
    await validateAndExecute(conversationIds, async () => {
      await executeOperation(
        'setPriority',
        () => bulkOperationsAPI.bulkSetPriority({ conversationIds, priority }),
        conversationIds.length
      )
    })
  }, [validateAndExecute, executeOperation])

  const addNotesToConversations = useCallback(async (
    conversationIds: string[], 
    note: string, 
    isPrivate: boolean = false
  ) => {
    await validateAndExecute(conversationIds, async () => {
      await executeOperation(
        'addNote',
        () => bulkOperationsAPI.bulkAddNote({ conversationIds, note, isPrivate }),
        conversationIds.length
      )
    })
  }, [validateAndExecute, executeOperation])

  const changeConversationStatus = useCallback(async (
    conversationIds: string[], 
    status: 'open' | 'pending' | 'resolved' | 'closed'
  ) => {
    await validateAndExecute(conversationIds, async () => {
      await executeOperation(
        'changeStatus',
        () => bulkOperationsAPI.bulkChangeStatus({ conversationIds, status }),
        conversationIds.length
      )
    })
  }, [validateAndExecute, executeOperation])

  return {
    state,
    assignConversations,
    archiveConversations,
    unsubscribeConversations,
    addTagsToConversations,
    removeTagsFromConversations,
    markConversationsAsRead,
    markConversationsAsUnread,
    closeConversationsAsRead,
    flagConversations,
    deleteConversations,
    restoreConversations,
    moveConversationsToSpam,
    exportConversations,
    setPriorityForConversations,
    addNotesToConversations,
    changeConversationStatus,
    resetState,
    validateAndExecute
  }
}

export default useBulkOperations
